<template>
  <DemoModal :show="isModalOpen" :url="brevoLink" @close="closeModal" />
  <!-- Añadimos un ref a la sección completa para el trigger general -->
  <div class="home-section2" ref="sectionRef">
    <div class="section-header">
      <h2 class="main-title" ref="titleRef">
        Un software de mantenimiento creado para
        <span class="highlight">tu operación</span>
      </h2>
      <p class="subtitle" ref="subtitleRef">
        Adapta todo lo que necesites para que Fourin Track se ajuste a tu negocio, y no al revés.
      </p>
    </div>

    <div class="features-grid">
      <div class="column-1">
        <!-- Añadimos un ref específico para "Optimiza" -->
        <div class="feature-box large-box" ref="optimizaRef">
          <img src="/src/assets/images/optimiza.png" alt="Optimiza" class="feature-image" />
        </div>

        <div class="bottom-row">
          <!-- Añadimos refs específicos para "Controla" y "Analiza" -->
          <div class="feature-box small-box controla" ref="controlaRef">
            <img src="/src/assets/images/controla.png" alt="Controla" class="feature-image" />
          </div>
          <div class="feature-box small-box analiza" ref="analizaRef">
            <img src="/src/assets/images/analiza.png" alt="Analiza" class="feature-image" />
          </div>
        </div>
      </div>

      <div class="column-2">
        <!-- Añadimos un ref específico para "Planifica" -->
        <div class="feature-box large-box" ref="planificaRef">
          <img src="/src/assets/images/planifica2.png" alt="Planifica" class="feature-image" />
        </div>
      </div>
    </div>

    <div class="cta-section" ref="ctaRef">
      <button class="cta-button" @click="openModal">Solicita una Demo →</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import DemoModal from './DemoModal.vue'
import { useDemoModal } from '../composables/useDemoModal'

gsap.registerPlugin(ScrollTrigger)

const { isModalOpen, brevoLink, openModal, closeModal } = useDemoModal()

// --- Referencias a los elementos del DOM ---
const sectionRef = ref<HTMLElement | null>(null)
const titleRef = ref<HTMLElement | null>(null)
const subtitleRef = ref<HTMLElement | null>(null)
const optimizaRef = ref<HTMLElement | null>(null)
const controlaRef = ref<HTMLElement | null>(null)
const analizaRef = ref<HTMLElement | null>(null)
const planificaRef = ref<HTMLElement | null>(null)
const ctaRef = ref<HTMLElement | null>(null)

onMounted(() => {
  nextTick(() => {
    // Creamos una única Timeline para toda la sección.
    // Esto nos da control total sobre la secuencia.
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.value,
        start: 'top 70%', // La animación empieza cuando la sección es 70% visible
        toggleActions: 'play none none none', // Se ejecuta una sola vez
      },
      defaults: {
        duration: 0.9,
        ease: 'power3.out', // Un easing natural y profesional
      },
    })

    // --- La Secuencia Narrativa ---

    // 1. Entra el Header (título y subtítulo)
    tl.from([titleRef.value, subtitleRef.value], {
      autoAlpha: 0,
      y: 40,
      stagger: 0.2, // El subtítulo entra un poco después del título
    })

    // 2. Entra "Optimiza" desde la izquierda
    tl.from(
      optimizaRef.value,
      {
        autoAlpha: 0,
        x: -50, // Desde la izquierda
      },
      '-=0.5',
    ) // Se solapa un poco con la animación anterior para más fluidez

    // 3. Entran "Controla" y "Analiza" desde abajo
    tl.from(
      [controlaRef.value, analizaRef.value],
      {
        autoAlpha: 0,
        y: 50, // Desde abajo
        stagger: 0.2, // Analiza entra un poco después de Controla
      },
      '-=0.5',
    )

    // 4. Entra "Planifica" desde la derecha
    tl.from(
      planificaRef.value,
      {
        autoAlpha: 0,
        x: 50, // Desde la derecha
      },
      '-=0.6',
    )

    // 5. Finalmente, aparece el botón CTA
    tl.from(
      ctaRef.value,
      {
        autoAlpha: 0,
        y: 20,
      },
      '-=0.4',
    )
  })
})
</script>

<style scoped>
.column-1 .small-box.analiza {
  width: -webkit-fill-available;
}

.column-1 .small-box.controla {
  width: -webkit-fill-available;
}

.column-1 .large-box {
  width: -webkit-fill-available;
}

.column-2 .large-box {
  width: -webkit-fill-available;
}

.feature-image {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.home-section2 {
  padding: 100px 20px;
  background: white;
  max-width: 1200px;
  margin: 0 auto;
  border-bottom: 2px solid;
  border-image: linear-gradient(to right, #ffffff, #e2e8f0, #ffffff) 1;
}

.section-header {
  text-align: center;
  display: flex;
  margin-bottom: 60px;
  flex-direction: column;
  align-items: center;
}

.main-title {
  font-size: 3rem;
  font-weight: 700;
  max-width: 800px;
  color: #1a202c;
  margin-bottom: 10px;
  line-height: 1.1;
}

.highlight {
  color: #129bef;
  font-weight: 700;
}

.subtitle {
  font-size: 1.125rem;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.features-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 60px;
}

.column-1 {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.column-2 {
  display: flex;
}

.large-box {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.small-box {
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.bottom-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.feature-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 15px;
}

.feature-description {
  font-size: 1rem;
  color: #4a5568;
  line-height: 1.6;
  margin: 0;
}

.cta-section {
  text-align: center;
}

.cta-button {
  background: #129bef;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 50px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cta-button:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
}
.feature-box {
  position: relative;
  overflow: hidden;
}

.feature-box::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  background: linear-gradient(90deg, transparent, rgba(18, 155, 239, 0.05), transparent);

  background-size: 200% 100%;
  background-position: -100% 0;

  transition: background-position 0.8s cubic-bezier(0.25, 1, 0.5, 1);
}

.feature-image {
  width: 100%;
  height: 100%;
  display: block;
}

.feature-box:hover {
  transform: scale(1.02);
}

.feature-box:hover::after {
  background-position: 200% 0;
}
/* Puedes añadir esta regla para evitar parpadeos (FOUC) */
/* GSAP la gestionará con autoAlpha */
.main-title,
.subtitle,
.feature-box {
  visibility: hidden;
}
@media (max-width: 768px) {
  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .feature-box::after {
    display: none;
  }
  .bottom-row {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .column-1 {
    gap: 10px;
  }

  .main-title {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 15px;
  }

  .home-section2 {
    padding: 60px 20px;
  }
}
</style>
